import {
  provide,
  inject,
  type InjectionKey,
  ref, onMounted,
  type VNodeRef,
} from 'vue'

import type { ResizeObserverCallback } from '@/composables/useResizeObserver'

import { useIntersectionObserver } from '@/composables/useIntersectionObserver'
import { useResizeObserver } from '@/composables/useResizeObserver'

type Store = {
  elementRef: VNodeRef
  elementWidth: number
  elementHeight: number
  visibleWidth: number
  visibleHeight: number
  isVisibleFull: boolean
}
const storeKey = {
  layoutHeader: Symbol('layoutHeader') as InjectionKey<Store>,
  layoutFooter: Symbol('layoutFooter') as InjectionKey<Store>,
  layoutAsideRight: Symbol('layoutAsideRight') as InjectionKey<Store>,
} as const
type StoreKey = keyof typeof storeKey

export const domSizeStoreProvide = (key: StoreKey) => provide(storeKey[key], shallowReactive({
  elementRef: ref<HTMLElement | null>(null),
  elementWidth: 0,
  elementHeight: 0,
  visibleWidth: 0,
  visibleHeight: 0,
  isVisibleFull: false,
}))

const domSizeStoreInject = (key: StoreKey) => {
  const store = inject(storeKey[key])
  if (!store) {
    throw new Error('store is not provided')
  }
  return store
}

export const useDomSizeStore = (key: StoreKey) => {
  const store = domSizeStoreInject(key)
  return {
    state: store,
    useObservers: () => {
      const isMounted = ref(false)
      onMounted(() => isMounted.value = true)
      onUnmounted(() => isMounted.value = false)

      const updateByResize: ResizeObserverCallback = ([entry]) => {
        if (!isMounted.value) return
        store.elementWidth = entry.target.clientWidth
        store.elementHeight = entry.target.clientHeight
      }

      const updateByIntersection: IntersectionObserverCallback = ([entry]) => {
        if (!isMounted.value) return
        if (!entry.isIntersecting) return
        store.visibleWidth = entry.intersectionRect.width
        store.visibleHeight = entry.intersectionRect.height
        store.isVisibleFull = entry.intersectionRatio >= 1
      }

      useResizeObserver(store.elementRef, updateByResize)
      useIntersectionObserver(store.elementRef, updateByIntersection, {
        threshold: Array.from(Array(101).keys(), i => i / 100),
      })
    },
  }
}
